import { type FC, Fragment, useCallback, useEffect, useMemo } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "redux";
import { Loader } from "semantic-ui-react";

import { LicenseTypes } from "enums";
import { type RootState } from "features/Application/globaltypes/redux";
import { fetchPackLicenseTypes } from "features/Licensing/Packs/state/thunks/packLicenseTypesThunk";
import { type PackLicenseType, type RequestedPackLicenses } from "features/Licensing/Packs/types/state";
import CardThumbnail from "components/cards/cardThumbnail/CardThumbnail";
import NoResults from "components/noResults/NoResults";
import { MAX_LICENSE_COUNT } from "features/Accounts/PackAssignmentModal/constants";
import {
  type AssignDomain,
  type AccountAvailablePackOverview,
  type AssignAccountPack,
} from "features/Accounts/PackAssignmentModal/types";
import { LicenseCountInput } from "features/Accounts/PackAssignmentModal/packAssignmentSteps/LicensesStep/LicenseCountInput/LicenseCountInput";

import styles from "features/Accounts/PackAssignmentModal/packAssignmentSteps/LicensesStep/LicensesStep.module.scss";
import LicensePicker from "components/LicensePicker/LicensePicker";
import FieldLabel from "components/forms/FieldLabel";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import DomainsSectionContainer from "./DomainsSectionContainer/DomainsSectionContainer";
import { ErrorPositions } from "enums/errorPositions";

interface PackAssigningInfoPropsOwn {
  renderModalActions: (nextStep: () => void, prevStep: () => void) => Function;
  header: string;
  selectedPacks: Array<AssignAccountPack>;
  items: Array<AccountAvailablePackOverview>;
  loadLicenseTypes?: () => void;
  onLicenseCountChange: (selectedPackId: number, licensesCount?: number) => void;
  onExpirationDateChange: (selectedPackId: number, optionId?: number, date?: Date | null) => void;
  validateFinishBtnEnabling: (maxLicenseCount: number) => void;
  isLoading: boolean;
  packRequestsData?: Map<number, RequestedPackLicenses>;
  error?: Error;
  isRestore?: boolean;
  showDomainSection?: boolean;
  accountId: number;
  selectedDomain?: AssignDomain;
}

export type PackAssigningInfoProps = PackAssigningInfoPropsOwn & PropsFromRedux;

export const LicensesAddingStep: FC<PackAssigningInfoProps> = ({
  selectedPacks,
  selectedDomain,
  items,
  loadLicenseTypes,
  licenseTypes,
  onLicenseCountChange,
  validateFinishBtnEnabling,
  onExpirationDateChange,
  isLoading,
  packRequestsData,
  error,
  isRestore,
  showDomainSection,
  accountId,
}) => {
  const isThreatDefenceDomainEnabled = !!useFeatureFlag(FeatureFlags.ThreatDefenceDomains);

  const finishButtonDependentField = [
    ...selectedPacks.map((p) => p.licensesCount),
    ...selectedPacks.map((p) => p.expirationDate),
    ...selectedPacks.map((p) => p.licenseType),
    selectedDomain,
  ];

  useEffect(() => {
    validateFinishBtnEnabling(MAX_LICENSE_COUNT);
    // eslint-disable-next-line
  }, finishButtonDependentField);

  useEffect(() => {
    if (licenseTypes.items.length === 0) {
      loadLicenseTypes();
    }
    // eslint-disable-next-line
  }, [licenseTypes.items]);

  const renderPackIdIsNotValid = () =>
    items.length === 0 && !isLoading && <NoResults title="Pack ID is not valid" iconClassName="fa-box-full" />;

  const getLicenseOptions = (item: AccountAvailablePackOverview | undefined, options: PackLicenseType[]) =>
    options
      .filter((option) => item?.isTrialAllowed || option.id !== LicenseTypes.Trial)
      .map((option: PackLicenseType) => {
        return {
          title: option.id === LicenseTypes.Trial ? `${item?.trialPeriod.name} ${option.name}` : option.name,
          id: option.id,
          duration: option.monthsCount,
        };
      })
      .reverse();

  const renderRestoreSection = () => (
    <div className={styles.restoreContainer} data-testid="restore-section">
      <div className={styles.restoreText}>
        This will restore all content and user data for this Pack placing it in an active state.
      </div>
      <div className={styles.restoreHeader}>Restore Licenses</div>
    </div>
  );

  const firstThreatDefencePackId = useMemo(
    () => selectedPacks.find((i) => i.isThreatDefencePack)?.packId,
    [selectedPacks],
  );

  const shouldShowDomain = useCallback(
    (pack?: AccountAvailablePackOverview, accountPack?: AssignAccountPack) => {
      const isTrialLicenseSelected = accountPack?.licenseType === LicenseTypes.Trial;
      const isThreatDefencePack = pack?.id === firstThreatDefencePackId && !pack?.isTrial;

      return isThreatDefenceDomainEnabled && showDomainSection && isThreatDefencePack && !isTrialLicenseSelected;
    },
    [isThreatDefenceDomainEnabled, firstThreatDefencePackId, showDomainSection],
  );

  const showBottomErrorPosition = selectedPacks.length > 2; // top error position looks bad if there are many packs. It is covered by the header due to overflow-y: scroll.

  return (
    <form className={styles.container} autoComplete="off" data-testid={"packs"}>
      {isRestore && renderRestoreSection()}
      {items.length > 0 &&
        !isLoading &&
        selectedPacks.map((accountPack: AssignAccountPack, index) => {
          const item = items.find((i) => i.id === accountPack.packId);
          const showDomain = shouldShowDomain(item, accountPack);
          return (
            <Fragment key={item?.id}>
              <div className={styles.item} key={item?.id}>
                <div className={styles.image}>
                  <CardThumbnail thumbnailUrl={item?.thumbnailImageUrl}>
                    <p className={styles.title}>{item?.title}</p>
                  </CardThumbnail>
                </div>

                <div className={styles.fields}>
                  <div className={styles.field}>
                    <LicenseCountInput
                      onLicenseCountChange={(selectedPackId: number, licensesCount?: number) => {
                        onLicenseCountChange(selectedPackId, licensesCount);
                      }}
                      packId={accountPack.packId}
                      licensesCount={accountPack.licensesCount}
                      assingedLicensesCount={accountPack.assignedLicensesCount}
                      maxLicenseCount={MAX_LICENSE_COUNT}
                      unlimitedLicenses={item?.unlimitedLicenses}
                      label={<FieldLabel label={"Licenses"} className={styles.label} markAsRequired />}
                      errorPosition={showBottomErrorPosition ? ErrorPositions.bottom : ErrorPositions.top}
                    />
                  </div>
                  {licenseTypes.items.length > 0 && (
                    <LicensePicker
                      options={getLicenseOptions(item, licenseTypes.items)}
                      onOptionChange={(optionId?: number) => {
                        onExpirationDateChange(accountPack.packId, optionId);
                      }}
                      onExpirationDateChange={(date?: null | Date) => {
                        onExpirationDateChange(accountPack.packId, undefined, date);
                      }}
                      preselectedOptionId={packRequestsData?.get(accountPack.packId)?.licenseTypeId}
                      preselectedExpirationDate={packRequestsData?.get(accountPack.packId)?.expirationDate}
                      verticalPosition={index > 0 ? "top" : "bottom"}
                      noExpiration={item?.noExpiration}
                      className={styles.field}
                      labelClassName={styles.label}
                    />
                  )}
                  {showDomain && <DomainsSectionContainer accountId={accountId} packId={accountPack.packId} />}
                </div>
              </div>
            </Fragment>
          );
        })}
      <Loader active={isLoading} />
      {error && (
        <div className={styles.error} data-testid="error-info">
          Error loading data!
        </div>
      )}
      {renderPackIdIsNotValid()}
    </form>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    licenseTypes: state.packs.packLicenseTypes,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: any) => ({
  loadLicenseTypes: bindActionCreators(fetchPackLicenseTypes, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(LicensesAddingStep);
export default ConnectedComponent;
